import * as Sentry from '@sentry/react'
import { useEffect, useMemo, useRef } from 'react'
import type { ProfilerOnRenderCallback } from 'react'

type MetricContext = Record<string, string | number | boolean | null | undefined>

type ActiveMetric = {
  name: string
  startedAt: number
  context?: MetricContext
}

const activeMetrics = new Map<string, ActiveMetric>()

const isBrowser = typeof window !== 'undefined'

const emitMetric = (name: string, durationMs: number, context?: MetricContext) => {
  if (!Number.isFinite(durationMs) || durationMs < 0) return

  const payload = {
    name,
    durationMs: Math.round(durationMs * 100) / 100,
    ...context,
  }

  if (isBrowser) {
    window.dispatchEvent(new CustomEvent('aboutwrk:performance', { detail: payload }))
  }

  Sentry.addBreadcrumb({
    category: 'performance',
    message: name,
    level: 'info',
    data: payload,
  })

  if (durationMs >= 150) {
    Sentry.captureMessage(`performance:${name}`, {
      level: 'info',
      contexts: {
        performance: payload,
      },
    })
  }
}

export const startTrackedMetric = (id: string, name: string, context?: MetricContext) => {
  activeMetrics.set(id, {
    name,
    startedAt: performance.now(),
    context,
  })
}

export const finishTrackedMetric = (id: string, context?: MetricContext) => {
  const activeMetric = activeMetrics.get(id)
  if (!activeMetric) return

  activeMetrics.delete(id)
  emitMetric(activeMetric.name, performance.now() - activeMetric.startedAt, {
    ...activeMetric.context,
    ...context,
  })
}

export const cancelTrackedMetric = (id: string) => {
  activeMetrics.delete(id)
}

export const useRouteReadyMetric = (routeKey: string) => {
  const metricId = useMemo(() => `route:${routeKey}`, [routeKey])

  useEffect(() => {
    startTrackedMetric(metricId, 'route.ready', { route: routeKey })

    const frame = window.requestAnimationFrame(() => {
      finishTrackedMetric(metricId, { route: routeKey })
    })

    return () => {
      window.cancelAnimationFrame(frame)
      cancelTrackedMetric(metricId)
    }
  }, [metricId, routeKey])
}

export const useFirstUsefulPaintMetric = (name: string, isReady: boolean, context?: MetricContext) => {
  const metricIdRef = useRef(`${name}:${Math.random().toString(36).slice(2)}`)
  const isReportedRef = useRef(false)

  useEffect(() => {
    startTrackedMetric(metricIdRef.current, name, context)
    return () => cancelTrackedMetric(metricIdRef.current)
  }, [name])

  useEffect(() => {
    if (!isReady || isReportedRef.current) return

    isReportedRef.current = true
    const frame = window.requestAnimationFrame(() => {
      finishTrackedMetric(metricIdRef.current, context)
    })

    return () => window.cancelAnimationFrame(frame)
  }, [isReady, context])
}

export const useListInteractionMetric = (
  name: string,
  isFetching: boolean,
  interactionKey: string,
  context?: MetricContext,
) => {
  const previousKeyRef = useRef<string | null>(null)
  const pendingMetricIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (previousKeyRef.current === null) {
      previousKeyRef.current = interactionKey
      return
    }

    if (previousKeyRef.current !== interactionKey) {
      const metricId = `${name}:${interactionKey}`
      pendingMetricIdRef.current = metricId
      previousKeyRef.current = interactionKey
      startTrackedMetric(metricId, name, context)
    }
  }, [interactionKey, name, context])

  useEffect(() => {
    if (isFetching || !pendingMetricIdRef.current) return

    const metricId = pendingMetricIdRef.current
    pendingMetricIdRef.current = null

    const frame = window.requestAnimationFrame(() => {
      finishTrackedMetric(metricId, context)
    })

    return () => window.cancelAnimationFrame(frame)
  }, [isFetching, context])
}

export const createRenderProfiler = (id: string, thresholdMs = 24): ProfilerOnRenderCallback => {
  return (profileId, phase, actualDuration, baseDuration) => {
    if (actualDuration < thresholdMs) return

    emitMetric('render.slow', actualDuration, {
      id,
      profileId,
      phase,
      actualDuration: Math.round(actualDuration * 100) / 100,
      baseDuration: Math.round(baseDuration * 100) / 100,
    })
  }
}
